import {
  USER_ROLES,
  USER_ROLE_NAMES,
  ORGANIZATION_TYPES,
  ORGANIZATION_TYPE_NAMES,
  TOKEN_STATUS,
  TOKEN_STATUS_NAMES,
  TRANSACTION_TYPES,
  TRANSACTION_TYPE_NAMES,
} from "@forestsync/db";

// Tailwind class mapping for each status (bg and text in one string)
export const TOKEN_STATUS_TW_CLASS = {
  [TOKEN_STATUS.ACTIVE]:
    "bg-green-100/40 dark:bg-green-400/10 text-green-700 dark:text-green-300",
  [TOKEN_STATUS.RETIRED]:
    "bg-gray-100/40 dark:bg-gray-400/10 text-gray-700 dark:text-gray-300", // Grayish for retired (like old spent)
  [TOKEN_STATUS.REVERSED]:
    "bg-red-100/40 dark:bg-red-400/10 text-red-700 dark:text-red-300", // Red for reversed (like old destroyed)
  [TOKEN_STATUS.INSURANCE_POOL]:
    "bg-purple-100/40 dark:bg-purple-400/10 text-purple-700 dark:text-purple-300", // Purple for insurance pool
  [TOKEN_STATUS.LOCKED]:
    "bg-orange-100/40 dark:bg-orange-400/10 text-orange-700 dark:text-orange-300", // Orange for in-settlement
  [TOKEN_STATUS.INSURANCE_RESERVED]:
    "bg-amber-100/40 dark:bg-amber-400/10 text-amber-700 dark:text-amber-300", // Amber for reserved (earmarked for replacement)
  // Backward compatibility for old enum values (if they still exist)
  ...(typeof (TOKEN_STATUS as any).SPENT !== "undefined" && {
    [(TOKEN_STATUS as any).SPENT]:
      "bg-gray-100/40 dark:bg-gray-400/10 text-gray-700 dark:text-gray-300",
  }),
  ...(typeof (TOKEN_STATUS as any).DESTROYED !== "undefined" && {
    [(TOKEN_STATUS as any).DESTROYED]:
      "bg-red-100/40 dark:bg-red-400/10 text-red-700 dark:text-red-300",
  }),
} as const;

// Glass-like hero background styles for passport page
export const TOKEN_STATUS_GLASS_BG = {
  [TOKEN_STATUS.ACTIVE]:
    "bg-gradient-to-br from-green-50/80 via-green-100/60 to-green-200/40 dark:from-green-900/20 dark:via-green-800/15 dark:to-green-700/10 backdrop-blur-sm border border-green-200/30 dark:border-green-700/30",
  [TOKEN_STATUS.RETIRED]:
    "bg-gradient-to-br from-gray-50/80 via-gray-100/60 to-gray-200/40 dark:from-gray-900/20 dark:via-gray-800/15 dark:to-gray-700/10 backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30",
  [TOKEN_STATUS.REVERSED]:
    "bg-gradient-to-br from-red-50/80 via-red-100/60 to-red-200/40 dark:from-red-900/20 dark:via-red-800/15 dark:to-red-700/10 backdrop-blur-sm border border-red-200/30 dark:border-red-700/30",
  [TOKEN_STATUS.INSURANCE_POOL]:
    "bg-gradient-to-br from-purple-50/80 via-purple-100/60 to-purple-200/40 dark:from-purple-900/20 dark:via-purple-800/15 dark:to-purple-700/10 backdrop-blur-sm border border-purple-200/30 dark:border-purple-700/30",
  [TOKEN_STATUS.LOCKED]:
    "bg-gradient-to-br from-orange-50/80 via-orange-100/60 to-orange-200/40 dark:from-orange-900/20 dark:via-orange-800/15 dark:to-orange-700/10 backdrop-blur-sm border border-orange-200/30 dark:border-orange-700/30",
  [TOKEN_STATUS.INSURANCE_RESERVED]:
    "bg-gradient-to-br from-amber-50/80 via-amber-100/60 to-amber-200/40 dark:from-amber-900/20 dark:via-amber-800/15 dark:to-amber-700/10 backdrop-blur-sm border border-amber-200/30 dark:border-amber-700/30",
  // Backward compatibility for old enum values (if they still exist)
  ...(typeof (TOKEN_STATUS as any).SPENT !== "undefined" && {
    [(TOKEN_STATUS as any).SPENT]:
      "bg-gradient-to-br from-gray-50/80 via-gray-100/60 to-gray-200/40 dark:from-gray-900/20 dark:via-gray-800/15 dark:to-gray-700/10 backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30",
  }),
  ...(typeof (TOKEN_STATUS as any).DESTROYED !== "undefined" && {
    [(TOKEN_STATUS as any).DESTROYED]:
      "bg-gradient-to-br from-red-50/80 via-red-100/60 to-red-200/40 dark:from-red-900/20 dark:via-red-800/15 dark:to-red-700/10 backdrop-blur-sm border border-red-200/30 dark:border-red-700/30",
  }),
} as const;

/**
 * Type-safe utility functions for converting between database enum values and display strings
 * Note: Display functions that return user-facing strings should be used with useTranslations hook
 */

// ===============================
// ====== Shared Result Types ====
// ===============================

export interface ActionResult<T = void> {
  success: boolean
  data?: T
  error?: string
  errorCode?: string
}

// Sanitize database/internal errors before returning to clients
// Strips raw Postgres messages, stack traces, and internal codes
export function sanitizeError(error: unknown, fallback = "An unexpected error occurred"): string {
  if (typeof error === "string") return fallback
  if (error instanceof Error) {
    const message = error.message
    // Suppress raw Postgres and internal implementation details
    if (
      message.includes("duplicate key") ||
      message.includes("violates") ||
      message.includes("syntax error") ||
      message.includes("ECONNREFUSED") ||
      message.includes("stack trace") ||
      message.match(/^ERROR:/)
    ) {
      return fallback
    }
    return message
  }
  return fallback
}


export function getUserRoleName(role: number): string {
  return USER_ROLE_NAMES[role as keyof typeof USER_ROLE_NAMES] || "UNKNOWN";
}

export function isProjectDeveloper(role: number): boolean {
  return role === USER_ROLES.PROJECT_DEVELOPER || role === USER_ROLES.BOTH;
}

export function isBeneficiary(role: number): boolean {
  return role === USER_ROLES.BENEFICIARY || role === USER_ROLES.BOTH;
}

// Organization type utilities
export function getOrganizationTypeName(type: number): string {
  return (
    ORGANIZATION_TYPE_NAMES[type as keyof typeof ORGANIZATION_TYPE_NAMES] ||
    "UNKNOWN"
  );
}

// Token status utilities
export function getTokenStatusName(status: number): string {
  return (
    TOKEN_STATUS_NAMES[status as keyof typeof TOKEN_STATUS_NAMES] || "UNKNOWN"
  );
}

// Organization type badge configuration
export interface OrganizationTypeBadge {
  label: string;
  color: "blue" | "cyan" | "teal" | "gray";
}

/**
 * Get organization type badge configuration using i18n translations
 * @param type - Organization type enum value
 * @param t - Translation function from useTranslations hook
 * @returns Badge configuration with translated label
 */
export function getOrganizationTypeBadge(
  type: number,
  t: (key: string) => string,
): OrganizationTypeBadge {
  const typeName = getOrganizationTypeName(type);

  switch (typeName) {
    case "INDIVIDUAL":
      return { label: t("common.individual"), color: "blue" };
    case "COMPANY":
      return { label: t("common.company"), color: "cyan" };
    case "ORGANIZATION":
      return { label: t("common.organization"), color: "teal" };
    default:
      return { label: t("common.unknown"), color: "gray" };
  }
}

/**
 * Get token status display name using i18n translations
 * @param status - Token status enum value
 * @param t - Translation function from useTranslations hook
 * @returns Translated status display name
 */
export function getTokenStatusDisplayName(
  status: number,
  t: (key: string) => string,
): string {
  const statusName = getTokenStatusName(status);

  switch (statusName) {
    case "ACTIVE":
      return t("common.statusLabels.active");
    case "SPENT":
    case "RETIRED":
      return t("common.statusLabels.spent"); // Maps to "Retired"
    case "DESTROYED":
    case "REVERSED":
      return t("common.statusLabels.destroyed"); // Maps to "Reversed"
    case "INSURANCE_POOL":
      return t("common.statusLabels.insurance_pool");
    default:
      return t("common.unknown");
  }
}

/**
 * Get organization type display name using i18n translations
 * @param type - Organization type enum value
 * @param t - Translation function from useTranslations hook
 * @returns Translated organization type display name
 */
export function getOrganizationTypeDisplayName(
  type: number,
  t: (key: string) => string,
): string {
  const typeName = getOrganizationTypeName(type);

  switch (typeName) {
    case "INDIVIDUAL":
      return t("common.individual");
    case "COMPANY":
      return t("common.company");
    case "ORGANIZATION":
      return t("common.organization");
    default:
      return t("common.unknown");
  }
}

/**
 * Get transaction type display name using i18n translations
 * @param type - Transaction type enum value
 * @param t - Translation function from useTranslations hook
 * @returns Translated transaction type display name
 */
export function getTransactionTypeDisplayName(
  tx: {
    fromUserId: string | number;
    toUserId: string | number;
    transactionType: number;
  },
  currentUserId: string | number,
  t: (key: string) => string,
): string {
  switch (tx.transactionType) {
    case TRANSACTION_TYPES.ISSUANCE:
      return t("common.transactionTypes.issuance");
    case TRANSACTION_TYPES.RETIREMENT:
      return t("common.transactionTypes.retirement");
    case TRANSACTION_TYPES.TRADE:
      if (String(tx.fromUserId) === String(currentUserId)) {
        return t("common.transactionTypes.sale");
      }
      if (String(tx.toUserId) === String(currentUserId)) {
        return t("common.transactionTypes.purchase");
      }
    default:
      return t("common.unknown");
  }
}

/**
 * Get simple transaction type name using i18n translations
 * @param type - Transaction type enum value
 * @param t - Translation function from useTranslations hook
 * @returns Translated transaction type display name
 */
export function getTransactionTypeName(
  type: number,
  t: (key: string) => string,
): string {
  switch (type) {
    case TRANSACTION_TYPES.ISSUANCE:
      return t("transaction.typeIssuance");
    case TRANSACTION_TYPES.TRADE:
      return t("transaction.typeTrade");
    case TRANSACTION_TYPES.RETIREMENT:
      return t("transaction.typeRetirement");
    default:
      return t("transaction.typeUnknown");
  }
}

/**
 * Get transaction type background color
 * @param type - Transaction type enum value
 * @returns Transaction type background color in tailwind class format
 */
export function getTransactionTypeBgColor(type: number): string {
  switch (type) {
    case TRANSACTION_TYPES.ISSUANCE:
      return "bg-gradient-to-br from-forest-50/80 via-forest-100/60 to-forest-200/40 dark:from-forest-900/20 dark:via-forest-800/15 dark:to-forest-700/10 backdrop-blur-sm border border-forest-200/30 dark:border-forest-700/30";
    case TRANSACTION_TYPES.TRADE:
      return "bg-gradient-to-br from-blue-50/80 via-blue-100/60 to-blue-200/40 dark:from-blue-900/20 dark:via-blue-800/15 dark:to-blue-700/10 backdrop-blur-sm border border-blue-200/30 dark:border-blue-700/30";
    case TRANSACTION_TYPES.RETIREMENT:
      return "bg-gradient-to-br from-earth-50/80 via-earth-100/60 to-earth-200/40 dark:from-earth-900/20 dark:via-earth-800/15 dark:to-earth-700/10 backdrop-blur-sm border border-earth-200/30 dark:border-earth-700/30";
    default:
      return "bg-gradient-to-br from-dark-50/80 via-dark-100/60 to-dark-200/40 dark:from-dark-900/20 dark:via-dark-800/15 dark:to-dark-700/10 backdrop-blur-sm border border-dark-200/30 dark:border-dark-700/30";
  }
}

// Admin-specific types and helpers (for form management)

export interface User {
  id: string
  name: string
  refId: string
  country: string
  role: number
}

export interface CarbonSink {
  id: string
  refId: string
  name: string
  country: string
  sizeHa: number
  co2Storage: number
  ownerId: string
}

export interface Token {
  id: string
  serialNumber: string
  currentOwnerId: string
  sinkName: string
  sinkRefId: string
  status: number
}

export interface SearchState<T> {
  options: T[]
  selected: T | null
  loading: boolean
  query: string
}

export interface LoadingState {
  loading: boolean
  error: string | null
}

// Data transformers
export const transformUser = (user: any): User => ({
  id: user.id,
  name: user.name,
  refId: user.refId || user.id,
  country: user.country,
  role: user.role
})

export const transformCarbonSink = (sink: any): CarbonSink => ({
  id: sink.id,
  refId: sink.refId,
  name: sink.name,
  country: sink.country,
  sizeHa: sink.sizeHa,
  co2Storage: Number(sink.co2Storage) || 0,
  ownerId: sink.ownerId || ""
})

// Render functions for consistent display
export const renderUserOption = (user: User): string => {
  return `${user.name} • ${user.refId} • ${user.country}`
}

export const renderUserSelected = (user: User): string => {
  return `${user.name} • ${user.refId}`
}

export const renderSinkOption = (sink: CarbonSink): string => {
  return `${sink.name} • ${sink.country} • ${sink.sizeHa} ha • ${(sink.co2Storage || 0).toLocaleString()} tons CO2`
}

export const renderSinkSelected = (sink: CarbonSink): string => {
  return `${sink.name} • ${sink.country}`
}

// Forest certification options for admin forms
export function getCertificationOptions() {
  return [
    { value: "FSC", label: "FSC (Forest Stewardship Council)" },
    { value: "PEFC", label: "PEFC (Programme for Endorsement of Forest Certification)" },
    { value: "SFI", label: "SFI (Sustainable Forestry Initiative)" },
    { value: "ATFS", label: "ATFS (American Tree Farm System)" },
    { value: "CSA", label: "CSA (Canadian Standards Association)" },
    { value: "VCS", label: "VCS (Verified Carbon Standard)" },
    { value: "GS", label: "Gold Standard" },
    { value: "CAR", label: "CAR (Climate Action Reserve)" },
  ] as const
}

// Supported SDG goals for admin forms (all 17 SDGs)
export function getSupportedSDGGoals() {
  return [
    { value: "1", label: "1. No Poverty" },
    { value: "2", label: "2. Zero Hunger" },
    { value: "3", label: "3. Good Health and Well-being" },
    { value: "4", label: "4. Quality Education" },
    { value: "5", label: "5. Gender Equality" },
    { value: "6", label: "6. Clean Water and Sanitation" },
    { value: "7", label: "7. Affordable and Clean Energy" },
    { value: "8", label: "8. Decent Work and Economic Growth" },
    { value: "9", label: "9. Industry, Innovation and Infrastructure" },
    { value: "10", label: "10. Reduced Inequalities" },
    { value: "11", label: "11. Sustainable Cities and Communities" },
    { value: "12", label: "12. Responsible Consumption and Production" },
    { value: "13", label: "13. Climate Action" },
    { value: "14", label: "14. Life Below Water" },
    { value: "15", label: "15. Life on Land" },
    { value: "16", label: "16. Peace, Justice and Strong Institutions" },
    { value: "17", label: "17. Partnerships to achieve the Goal" },
  ] as const
}

// Fixed permanence presets aligned with VCM registry standards.
// Values represent declared minimum commitment years; free input is not allowed
// because registries require recognised thresholds (Puro 100yr, Verra 100yr buffer, etc.).
export const LIFESPAN_OPTIONS = [
  { value: "10", label: "10+" },
  { value: "20", label: "20+" },
  { value: "50", label: "50+" },
  { value: "100", label: "100+" },
  { value: "200", label: "200+" },
  { value: "1000", label: "1000+" },
] as const

export function getUserRoleBadge(role: number, t: any) {
  switch (role) {
    case USER_ROLES.BENEFICIARY:
      return { label: t("dashboard.userRole.beneficiary"), color: "timber" }
    case USER_ROLES.PROJECT_DEVELOPER:
      return { label: t("dashboard.userRole.projectDeveloper"), color: "sage" }
    case USER_ROLES.BOTH:
      return { label: t("dashboard.userRole.both"), color: "forest" }
    default:
      return { label: t("common.unknown"), color: "gray" }
  }
}
